// li-funnel-redesign v2 — the payment page in the Duolingo style. The shared
// `createStripeSubscriptionAction` hard-redirects to /checkout/payment, so this
// page cannot live under the variant prefix and takes no props: it reads the
// client secret, plan and prices itself exactly like the shared payment page.

import CheckoutForm from '@app-studio-web/ui/components/CheckoutForm'
import type { GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import type { Plan } from '@app-studio-web/ui/types'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, StripeElementLocale } from '@stripe/stripe-js'
import type { User } from '@supabase/supabase-js'
import { useTranslation } from 'react-i18next'
import { Navigate, useLoaderData, useSearchParams } from 'react-router-dom'

// Locales Stripe Elements can localize placeholders and validation messages in.
// Kept in sync with the StripeElementLocale union from @stripe/stripe-js.
const stripeSupportedLocales = new Set<StripeElementLocale>([
  'ar',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'et',
  'fi',
  'fil',
  'fr',
  'he',
  'hr',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'lt',
  'lv',
  'ms',
  'mt',
  'nb',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'th',
  'tr',
  'vi',
  'zh',
])

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
)

export default function PaymentPage() {
  const [searchParams] = useSearchParams()
  const [pricesData, user] = useLoaderData() as [GetPricesData, User]

  const clientSecret = searchParams.get('client_secret')!
  const currency = searchParams.get('currency')!
  const intentMode =
    searchParams.get('intent_mode') === 'setup' ? 'setup' : 'payment'
  const plan = searchParams.get('plan')! as Plan
  const subscriptionId = searchParams.get('subscription_id') ?? ''
  const trialPeriodDaysParam = searchParams.get('trial_period_days')
  const trialPeriodDays = trialPeriodDaysParam
    ? Number.parseInt(trialPeriodDaysParam, 10)
    : undefined

  const { i18n, t } = useTranslation()

  const stripeLocale: StripeElementLocale = stripeSupportedLocales.has(
    i18n.language as StripeElementLocale,
  )
    ? (i18n.language as StripeElementLocale)
    : 'auto'

  if (!clientSecret) return <Navigate replace to="/checkout/plans" />

  return (
    <div className="min-h-dvh bg-white">
      <main className="duo-container flex min-h-dvh flex-col gap-6 py-10">
        <div className="border-duo-line space-y-2 rounded-2xl border-2 border-b-4 px-4 py-4">
          <p className="text-duo-ink text-[15px] font-extrabold">
            {t('pages.checkout.payment.securePaymentTitle')}
          </p>

          <p className="text-duo-muted text-sm leading-relaxed">
            {t('pages.checkout.payment.securePaymentText')}
          </p>

          <p className="text-duo-ink pt-2 text-[15px] font-extrabold">
            {t('pages.checkout.payment.moneyBackGuaranteeTitle')}
          </p>

          <p className="text-duo-muted text-sm leading-relaxed">
            {t('pages.checkout.payment.moneyBackGuaranteeText')}
          </p>
        </div>

        <div className="grow">
          <Elements
            options={{
              appearance: {
                labels: 'floating',
                theme: 'flat',
                variables: {
                  borderRadius: '16px',
                  colorBackground: '#FFFFFF',
                  colorDanger: '#FF4B4B',
                  colorPrimary: '#006BFF',
                  colorText: '#3C3C3C',
                  fontFamily: getComputedStyle(document.documentElement)
                    .getPropertyValue('--font-sans')
                    .trim(),
                },
              },
              clientSecret,
              locale: stripeLocale,
            }}
            stripe={stripePromise}
          >
            <CheckoutForm
              clientSecret={clientSecret}
              currency={currency}
              intentMode={intentMode}
              plan={plan}
              pricesData={pricesData}
              subscriptionId={subscriptionId}
              trialPeriodDays={trialPeriodDays}
              userEmail={user?.user_metadata?.original_email ?? ''}
            />
          </Elements>
        </div>
      </main>
    </div>
  )
}
